import React, { useMemo, useCallback, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import ArrowIconLeft from 'mdi-react/ChevronLeftIcon';
import ArrowIconRight from 'mdi-react/ChevronRightIcon';
import { PaginationItem as PaginationItemType } from './pagination-items';
import * as S from './styles';

type Props = {
  item: PaginationItemType;
  linkToPage: (page: number) => string;
  onClick?: (item: PaginationItemType) => void;
};

const PaginationItem = ({ item, linkToPage, onClick }: Props) => {
  const { t } = useContext(AppContext);
  const label = useMemo(() => {
    switch (item.type) {
      case 'prev':
        return <ArrowIconLeft />;
      case 'next':
        return <ArrowIconRight />;
      default:
        return <>{item.pageNumber}</>;
    }
  }, [item.type, item.pageNumber]);
  const ariaLabel = useMemo(() => {
    switch (item.type) {
      case 'prev':
        return t('symbols', 'symbol.show-prev');
      case 'next':
        return t('symbols', 'symbol.show-next');
      default:
        return t('common', 'pagination.page-number', {
          number: item.pageNumber,
        });
    }
  }, [item.type, item.pageNumber, t]);
  const onClickHandler = useCallback(
    () => typeof onClick === 'function' && onClick(item),
    [item, onClick]
  );

  if (item.disabled) {
    return <S.DisabledPaginationLink>{label}</S.DisabledPaginationLink>;
  }

  if (item.isCurrent) {
    return (
      <S.CurrentPaginationLink data-test="CurrentPaginationLink">
        {label}
      </S.CurrentPaginationLink>
    );
  }

  return (
    <S.PaginationLink
      to={linkToPage(item.pageNumber)}
      data-test="PaginationLink"
      data-test-page={item.pageNumber}
      onClick={onClickHandler}
      aria-label={ariaLabel}
    >
      {label}
    </S.PaginationLink>
  );
};

export default PaginationItem;
